import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { observer } from 'mobx-react'

import {
  IConditionalField,
  IPermitTypeField,
  ITypeFieldFilterOptions,
  LocationType,
  PermitFieldType,
} from '~/client/graph/types.generated'
import {
  parseConditionalKeyIds,
  serializeConditionalKey,
} from '~/client/src/shared/utils/conditionalKeyUtils'

import FieldValuesSelector from '../../../../FieldConfigurator/components/DynamicFilters/components/DynamicFiltersList/components/FieldValuesSelector'
import IDependentFieldsConfiguratorProps from '../types/IDependentFieldsConfiguratorProps'
import IDependentFieldsHandlersProps from '../types/IDependentFieldsHandlersProps'
import ConditionalFieldList from './ConditionalFieldList'
import ConditionalRuleHeader from './ConditionalRuleHeader'

// localization: translated

interface IProps {
  rule: IConditionalField
  field: IPermitTypeField
  isExpanded: boolean
  isLBSToggleShown: boolean
  usesPickerFlow?: boolean
  depthLevel: number
  restrictedFieldTypes: PermitFieldType[]
  restrictedLocationIds?: string[]
  allUsedLocationIds?: string[]
  restrictedLocationTypes?: LocationType[]

  onToggleRule: (ruleKey: string) => void
  getRuleDisplayName: (key: string) => string

  handlers: IDependentFieldsHandlersProps
  mainContainerProps: IDependentFieldsConfiguratorProps

  tableConfiguratorRenderer: (field: IPermitTypeField) => JSX.Element
}

const SCROLL_DELAY = 100

function ConditionalRule({
  rule,
  field,
  isExpanded,
  isLBSToggleShown,
  usesPickerFlow,
  depthLevel,
  restrictedFieldTypes,
  restrictedLocationIds,
  allUsedLocationIds,
  restrictedLocationTypes,
  mainContainerProps,
  onToggleRule,
  getRuleDisplayName,
  handlers,
  tableConfiguratorRenderer,
}: IProps) {
  const ruleRef = useRef<HTMLDivElement>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [draftIds, setDraftIds] = useState<string[]>([])

  useEffect(() => {
    if (isExpanded && ruleRef?.current) {
      const ref = ruleRef.current
      setTimeout(() => {
        ref?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        })
      }, SCROLL_DELAY)
    }
  }, [isExpanded])

  // Exit edit mode when collapsed externally
  if (isEditMode && !isExpanded) {
    setIsEditMode(false)
  }

  // Parse key once per render, reuse everywhere
  const ruleKeyIds = useMemo(() => parseConditionalKeyIds(rule.key), [rule.key])

  const handleEditToggle = useCallback(() => {
    if (isEditMode) {
      if (draftIds.length === 0) return

      onToggleRule(rule.key)

      const newKey = serializeConditionalKey(draftIds)
      if (newKey !== rule.key) {
        handlers.handleEditRuleLocations(rule.key, draftIds)
      }
      setIsEditMode(false)
    } else {
      setDraftIds(ruleKeyIds)
      setIsEditMode(true)
      if (!isExpanded) {
        onToggleRule(rule.key)
      }
    }
  }, [
    isEditMode,
    draftIds,
    rule.key,
    ruleKeyIds,
    handlers,
    isExpanded,
    onToggleRule,
  ])

  const displayIds = isEditMode ? draftIds : ruleKeyIds

  const fieldValuesFilter = useMemo(
    () =>
      ({
        fieldId: field.id,
        fieldValues: displayIds,
      }) as ITypeFieldFilterOptions,
    [field.id, displayIds],
  )

  const pickerRestrictedIds = useMemo(() => {
    if (!restrictedLocationIds || !allUsedLocationIds)
      return restrictedLocationIds || []
    const ownIds = new Set(ruleKeyIds)
    const otherUsedIds = allUsedLocationIds.filter(id => !ownIds.has(id))
    return [...restrictedLocationIds, ...otherUsedIds]
  }, [restrictedLocationIds, allUsedLocationIds, ruleKeyIds])

  const valueSelector = usesPickerFlow ? (
    <FieldValuesSelector
      filter={fieldValuesFilter}
      selectedField={field}
      isEditMode={isEditMode}
      restrictedLocationIds={pickerRestrictedIds}
      restrictedLocationTypes={restrictedLocationTypes || []}
      onChange={setDraftIds}
    />
  ) : undefined

  return (
    <div
      ref={ruleRef}
      className="mb12 ba-palette-brand-lighter brada8 mw-fit-content"
    >
      <ConditionalRuleHeader
        rule={rule}
        isExpanded={isExpanded}
        field={field}
        selectedValueCaption={getRuleDisplayName(rule.key)}
        renderValueSelector={valueSelector}
        isPickerEditMode={usesPickerFlow && isEditMode}
        isEditDisabled={isEditMode && draftIds.length === 0}
        onEditToggle={usesPickerFlow ? handleEditToggle : undefined}
        onToggleRule={onToggleRule}
        onRemoveRule={() => handlers.handleRemoveRule(rule.key)}
      />

      {isExpanded && (
        <ConditionalFieldList
          rule={rule}
          isLBSToggleShown={isLBSToggleShown}
          depthLevel={depthLevel}
          restrictedFieldTypes={restrictedFieldTypes}
          mainContainerProps={mainContainerProps}
          handlers={handlers}
          tableConfiguratorRenderer={tableConfiguratorRenderer}
        />
      )}
    </div>
  )
}

export default observer(ConditionalRule)
